import { z } from "zod";
import type { LeadRouting, LeadRoutingSettings, LeadRoutingUpdate } from "./lead-types";

export const ROUTING_MODES: readonly LeadRouting[] = ["round_robin", "default_owner"];
const EMAIL_MAX = 254;

/** Dialog form shape: the owner is a user id, or "" while nobody is chosen. */
export interface RoutingValues {
  routing: LeadRouting;
  owner: string;
  email: string;
}

export interface RoutingMessages {
  ownerRequired: string;
  emailInvalid: string;
}

export function routingFromSettings(settings: LeadRoutingSettings): RoutingValues {
  return { routing: settings.routing, owner: settings.default_owner_user_id ?? "", email: settings.notify_email ?? "" };
}

const isEmail = (value: string) => z.string().email().safeParse(value).success;

/** Same rules as backend `LeadRoutingService`: "same person" needs an owner; the inbox is a valid email. */
export function routingSchema(messages: RoutingMessages) {
  return z
    .object({
      routing: z.enum(["round_robin", "default_owner"]),
      owner: z.string(),
      email: z.string().trim().max(EMAIL_MAX, messages.emailInvalid).refine((value) => value === "" || isEmail(value), messages.emailInvalid),
    })
    .superRefine((values, ctx) => {
      if (values.routing === "default_owner" && values.owner === "") ctx.addIssue({ code: "custom", path: ["owner"], message: messages.ownerRequired });
    });
}

/** `PUT /v1/settings/leads` body: round-robin carries no owner, a blank inbox is `null`. */
export function toRoutingUpdate(values: RoutingValues): LeadRoutingUpdate {
  return {
    routing: values.routing,
    default_owner_user_id: values.routing === "default_owner" ? values.owner : null,
    notify_email: values.email.trim() || null,
  };
}
